import { Field } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationCircle } from "react-icons/fa";
import {
  getArBuildings,
  getArDistricts,
  getArGeonims,
  getArLocalities,
  getArPremises,
  getArRegions,
  mapArOptions,
} from "../../../common/api/arApi";
import SelectCustomFormik from "../../../shared/Ui/SelectCustom/SelectCustomFormik";
import style from "./DocumentInventoryWriteOff.module.css";

export const EMPTY_SALE_FIELDS = {
  buyerIin: null as string | null,
  saleSAtsRegionId: null as number | null,
  saleSAtsRegionName: null as string | null,
  saleSAtsDistrictId: null as number | null,
  saleSAtsDistrictName: null as string | null,
  saleSAtsLocalityId: null as number | null,
  saleSAtsLocalityName: null as string | null,
  saleSGeonimId: null as number | null,
  saleSGeonimName: null as string | null,
  saleSBuildingId: null as number | null,
  saleSBuildingName: null as string | null,
  saleSBuildingRca: null as string | null,
  saleSPbId: null as number | null,
  saleSPbName: null as string | null,
  saleSPbRca: null as string | null,
  _saleRegionTypeCode: null as number | null,
};

/** Город респ. значения — нас. пункт не обязателен, улицы от района */
export const REPUBLICAN_CITY_TYPE_CODE = 9;

type Option = {
  value: number;
  label: string;
  typeCode?: number;
  name?: string;
  displayName?: string;
  shortValue?: string;
  rca?: string;
};

type Props = {
  formik: any;
  disabled: boolean;
  isGasGroup: boolean;
  onDirty: () => void;
};

/** Район/город, нас. пункт, улица — мин. 3 символа; здание/помещение — без минимума */
const MIN_QUERY_TEXT = 3;

function debounce<T extends (...args: any[]) => void>(fn: T, wait: number) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const wrapped = (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
  wrapped.cancel = () => {
    if (timer) clearTimeout(timer);
  };
  return wrapped;
}

function seedOption(id: any, name: any): Option[] {
  if (id == null || id === "" || !name) return [];
  return [{ value: Number(id), label: String(name) }];
}

function FieldError({ touched, error, fallback }: { touched?: boolean; error?: any; fallback: string }) {
  if (!touched || !error) return null;
  return (
    <div className={style.tooltipWrapper}>
      <FaExclamationCircle className={style.errorIcon} />
      <div className={style.tooltipText}>
        {typeof error === "string" ? error : fallback}
      </div>
    </div>
  );
}

export default function SaleAddressFields({
  formik,
  disabled,
  isGasGroup,
  onDirty,
}: Props) {
  const { t } = useTranslation();
  const requiredMark = isGasGroup ? " *" : "";

  const [regionOptions, setRegionOptions] = useState<Option[]>(() =>
    seedOption(formik.values.saleSAtsRegionId, formik.values.saleSAtsRegionName),
  );
  const [districtOptions, setDistrictOptions] = useState<Option[]>(() =>
    seedOption(formik.values.saleSAtsDistrictId, formik.values.saleSAtsDistrictName),
  );
  const [localityOptions, setLocalityOptions] = useState<Option[]>(() =>
    seedOption(formik.values.saleSAtsLocalityId, formik.values.saleSAtsLocalityName),
  );
  const [geonimOptions, setGeonimOptions] = useState<Option[]>(() =>
    seedOption(formik.values.saleSGeonimId, formik.values.saleSGeonimName),
  );
  const [buildingOptions, setBuildingOptions] = useState<Option[]>(() =>
    seedOption(formik.values.saleSBuildingId, formik.values.saleSBuildingName),
  );
  const [premiseOptions, setPremiseOptions] = useState<Option[]>(() =>
    seedOption(formik.values.saleSPbId, formik.values.saleSPbName),
  );

  const [regionTypeCode, setRegionTypeCode] = useState<number | null>(
    formik.values._saleRegionTypeCode ?? null,
  );
  const seededRef = useRef(false);

  useEffect(() => {
    if (formik.values._saleRegionTypeCode != null) {
      setRegionTypeCode(formik.values._saleRegionTypeCode);
    }
  }, [formik.values._saleRegionTypeCode]);

  useEffect(() => {
    if (seededRef.current) return;
    if (formik.values.saleSAtsRegionId && formik.values.saleSAtsRegionName) {
      setRegionOptions(seedOption(formik.values.saleSAtsRegionId, formik.values.saleSAtsRegionName));
      seededRef.current = true;
    }
  }, [formik.values.saleSAtsRegionId, formik.values.saleSAtsRegionName]);

  const isRepublicanCity =
    (regionTypeCode ?? formik.values._saleRegionTypeCode) === REPUBLICAN_CITY_TYPE_CODE;
  const skipLocality = isRepublicanCity;

  const atsIdForGeonims = useMemo(() => {
    if (skipLocality) return formik.values.saleSAtsDistrictId;
    return formik.values.saleSAtsLocalityId;
  }, [skipLocality, formik.values.saleSAtsDistrictId, formik.values.saleSAtsLocalityId]);

  const clearFrom = (level: "district" | "locality" | "geonim" | "building" | "premise") => {
    const patch: any = {};
    if (level === "district" || level === "locality" || level === "geonim" || level === "building" || level === "premise") {
      if (level === "district") {
        Object.assign(patch, {
          saleSAtsDistrictId: null,
          saleSAtsDistrictName: null,
          saleSAtsLocalityId: null,
          saleSAtsLocalityName: null,
          saleSGeonimId: null,
          saleSGeonimName: null,
          saleSBuildingId: null,
          saleSBuildingName: null,
          saleSBuildingRca: null,
          saleSPbId: null,
          saleSPbName: null,
          saleSPbRca: null,
        });
        setDistrictOptions([]);
        setLocalityOptions([]);
        setGeonimOptions([]);
        setBuildingOptions([]);
        setPremiseOptions([]);
      } else if (level === "locality") {
        Object.assign(patch, {
          saleSAtsLocalityId: null,
          saleSAtsLocalityName: null,
          saleSGeonimId: null,
          saleSGeonimName: null,
          saleSBuildingId: null,
          saleSBuildingName: null,
          saleSBuildingRca: null,
          saleSPbId: null,
          saleSPbName: null,
          saleSPbRca: null,
        });
        setLocalityOptions([]);
        setGeonimOptions([]);
        setBuildingOptions([]);
        setPremiseOptions([]);
      } else if (level === "geonim") {
        Object.assign(patch, {
          saleSGeonimId: null,
          saleSGeonimName: null,
          saleSBuildingId: null,
          saleSBuildingName: null,
          saleSBuildingRca: null,
          saleSPbId: null,
          saleSPbName: null,
          saleSPbRca: null,
        });
        setGeonimOptions([]);
        setBuildingOptions([]);
        setPremiseOptions([]);
      } else if (level === "building") {
        Object.assign(patch, {
          saleSBuildingId: null,
          saleSBuildingName: null,
          saleSBuildingRca: null,
          saleSPbId: null,
          saleSPbName: null,
          saleSPbRca: null,
        });
        setBuildingOptions([]);
        setPremiseOptions([]);
      } else {
        Object.assign(patch, {
          saleSPbId: null,
          saleSPbName: null,
          saleSPbRca: null,
        });
        setPremiseOptions([]);
      }
    }
    formik.setValues({ ...formik.values, ...patch });
  };

  const searchRegions = useCallback(
    debounce(async (input: string) => {
      if (!input || input.length < MIN_QUERY_TEXT) {
        setRegionOptions(seedOption(formik.values.saleSAtsRegionId, formik.values.saleSAtsRegionName));
        return;
      }
      try {
        const data = await getArRegions(input);
        setRegionOptions(mapArOptions(data));
      } catch (e) {
        console.error(e);
        setRegionOptions([]);
      }
    }, 400),
    [formik.values.saleSAtsRegionId, formik.values.saleSAtsRegionName],
  );

  const searchDistricts = useCallback(
    debounce(async (input: string) => {
      const parentId = formik.values.saleSAtsRegionId;
      if (!parentId || !input || input.length < MIN_QUERY_TEXT) {
        setDistrictOptions(seedOption(formik.values.saleSAtsDistrictId, formik.values.saleSAtsDistrictName));
        return;
      }
      try {
        const data = await getArDistricts(parentId, input);
        setDistrictOptions(mapArOptions(data));
      } catch (e) {
        console.error(e);
        setDistrictOptions([]);
      }
    }, 400),
    [formik.values.saleSAtsRegionId, formik.values.saleSAtsDistrictId, formik.values.saleSAtsDistrictName],
  );

  const searchLocalities = useCallback(
    debounce(async (input: string) => {
      const parentId = formik.values.saleSAtsDistrictId;
      if (!parentId || !input || input.length < MIN_QUERY_TEXT) {
        setLocalityOptions(seedOption(formik.values.saleSAtsLocalityId, formik.values.saleSAtsLocalityName));
        return;
      }
      try {
        const data = await getArLocalities(parentId, input);
        setLocalityOptions(mapArOptions(data));
      } catch (e) {
        console.error(e);
        setLocalityOptions([]);
      }
    }, 400),
    [formik.values.saleSAtsDistrictId, formik.values.saleSAtsLocalityId, formik.values.saleSAtsLocalityName],
  );

  const searchGeonims = useCallback(
    debounce(async (input: string) => {
      const atsId = atsIdForGeonims;
      if (!atsId || !input || input.length < MIN_QUERY_TEXT) {
        setGeonimOptions(seedOption(formik.values.saleSGeonimId, formik.values.saleSGeonimName));
        return;
      }
      try {
        const data = await getArGeonims(atsId, input);
        setGeonimOptions(mapArOptions(data));
      } catch (e) {
        console.error(e);
        setGeonimOptions([]);
      }
    }, 400),
    [atsIdForGeonims, formik.values.saleSGeonimId, formik.values.saleSGeonimName],
  );

  const loadBuildings = useCallback(async (geonimId: number | null, query = "") => {
    if (!geonimId) {
      setBuildingOptions([]);
      return;
    }
    try {
      const data = await getArBuildings(geonimId, query);
      setBuildingOptions(mapArOptions(data));
    } catch (e) {
      console.error(e);
      setBuildingOptions([]);
    }
  }, []);

  const loadPremises = useCallback(async (buildingId: number | null, query = "") => {
    if (!buildingId) {
      setPremiseOptions([]);
      return;
    }
    try {
      const data = await getArPremises(buildingId, query);
      setPremiseOptions(mapArOptions(data));
    } catch (e) {
      console.error(e);
      setPremiseOptions([]);
    }
  }, []);

  /** При выборе улицы — сразу все здания; при выборе здания — все помещения */
  useEffect(() => {
    const geonimId = formik.values.saleSGeonimId;
    if (!geonimId) {
      setBuildingOptions([]);
      return;
    }
    loadBuildings(Number(geonimId), "");
  }, [formik.values.saleSGeonimId, loadBuildings]);

  useEffect(() => {
    const buildingId = formik.values.saleSBuildingId;
    if (!buildingId) {
      setPremiseOptions([]);
      return;
    }
    loadPremises(Number(buildingId), "");
  }, [formik.values.saleSBuildingId, loadPremises]);

  const searchBuildings = useCallback(
    debounce(async (input: string) => {
      const geonimId = formik.values.saleSGeonimId;
      if (!geonimId) {
        setBuildingOptions(seedOption(formik.values.saleSBuildingId, formik.values.saleSBuildingName));
        return;
      }
      await loadBuildings(Number(geonimId), input || "");
    }, 400),
    [
      formik.values.saleSGeonimId,
      formik.values.saleSBuildingId,
      formik.values.saleSBuildingName,
      loadBuildings,
    ],
  );

  const searchPremises = useCallback(
    debounce(async (input: string) => {
      const buildingId = formik.values.saleSBuildingId;
      if (!buildingId) {
        setPremiseOptions(seedOption(formik.values.saleSPbId, formik.values.saleSPbName));
        return;
      }
      await loadPremises(Number(buildingId), input || "");
    }, 400),
    [
      formik.values.saleSBuildingId,
      formik.values.saleSPbId,
      formik.values.saleSPbName,
      loadPremises,
    ],
  );

  const findOpt = (options: Option[], id: any) =>
    options.find((o) => o.value === id || String(o.value) === String(id));

  const noOptionsMessageText = ({ inputValue }: { inputValue: string }) => {
    if (!inputValue || inputValue.length < MIN_QUERY_TEXT) {
      return t("document_inventory_write_off.ar_min_chars", { count: MIN_QUERY_TEXT });
    }
    return t("document_inventory_write_off.ar_not_found");
  };

  const noOptionsMessageFree = () => t("document_inventory_write_off.ar_not_found");

  const localityRequired = isGasGroup && !skipLocality;

  return (
    <div className="flex flex-col gap-[20px]">
      <div className="flex gap-[20px] flex-wrap max-w-[950px]">
        <div className={style.inputField}>
          <div className={style.labelAndButtonWrapper}>
            <label className={style.inputLabel2}>
              {t("document_inventory_write_off.buyer_iin")}
              {isGasGroup ? " *" : ""}
            </label>
            <FieldError
              touched={formik.touched.buyerIin}
              error={formik.errors.buyerIin}
              fallback={t("documentsError.required")}
            />
          </div>
          <Field
            type="text"
            name="buyerIin"
            placeholder={t("document_inventory_write_off.buyer_iin_placeholder")}
            className={`${style.placeholder} ${
              formik.touched.buyerIin && formik.errors.buyerIin ? "border-red-500" : ""
            }`}
            value={formik.values.buyerIin || ""}
            disabled={disabled}
            maxLength={12}
            onChange={(e: any) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 12);
              formik.setFieldValue("buyerIin", value);
              onDirty();
            }}
          />
        </div>
      </div>

      <div className="text-base font-semibold text-gray-700">
        {t("document_inventory_write_off.sale_address_title")}
      </div>

      <div className="flex gap-[20px] flex-wrap">
        <div className={style.inputField}>
          <div className={style.labelAndButtonWrapper}>
            <label className={style.inputLabel2}>
              {t("document_inventory_write_off.sale_region")}
              {requiredMark}
            </label>
            <FieldError
              touched={formik.touched.saleSAtsRegionId}
              error={formik.errors.saleSAtsRegionId}
              fallback={t("documentsError.required")}
            />
          </div>
          <Field
            component={SelectCustomFormik}
            name="saleSAtsRegionId"
            placeholder={t("documentLabels.choose")}
            size="sm"
            isMulti={false}
            options={regionOptions}
            isDisabled={disabled}
            isError={Boolean(formik.touched.saleSAtsRegionId && formik.errors.saleSAtsRegionId)}
            onInputChange={searchRegions}
            noOptionsMessage={noOptionsMessageText}
            onChange={(id: any) => {
              const opt = findOpt(regionOptions, id);
              setRegionTypeCode(opt?.typeCode ?? null);
              formik.setValues({
                ...formik.values,
                ...EMPTY_SALE_FIELDS,
                buyerIin: formik.values.buyerIin,
                saleSAtsRegionId: id ?? null,
                saleSAtsRegionName: opt?.displayName || opt?.name || opt?.label || null,
                _saleRegionTypeCode: opt?.typeCode ?? null,
              });
              setDistrictOptions([]);
              setLocalityOptions([]);
              setGeonimOptions([]);
              setBuildingOptions([]);
              setPremiseOptions([]);
              onDirty();
            }}
          />
        </div>

        <div className={style.inputField}>
          <div className={style.labelAndButtonWrapper}>
            <label className={style.inputLabel2}>
              {t("document_inventory_write_off.sale_district")}
              {requiredMark}
            </label>
            <FieldError
              touched={formik.touched.saleSAtsDistrictId}
              error={formik.errors.saleSAtsDistrictId}
              fallback={t("documentsError.required")}
            />
          </div>
          <Field
            component={SelectCustomFormik}
            name="saleSAtsDistrictId"
            placeholder={t("documentLabels.choose")}
            size="sm"
            isMulti={false}
            options={districtOptions}
            isDisabled={disabled || !formik.values.saleSAtsRegionId}
            isError={Boolean(formik.touched.saleSAtsDistrictId && formik.errors.saleSAtsDistrictId)}
            onInputChange={searchDistricts}
            noOptionsMessage={noOptionsMessageText}
            onChange={(id: any) => {
              const opt = findOpt(districtOptions, id);
              clearFrom("locality");
              formik.setFieldValue("saleSAtsDistrictId", id ?? null);
              formik.setFieldValue(
                "saleSAtsDistrictName",
                opt?.displayName || opt?.name || opt?.label || null,
              );
              onDirty();
            }}
          />
        </div>

        {!skipLocality && (
          <div className={style.inputField}>
            <div className={style.labelAndButtonWrapper}>
              <label className={style.inputLabel2}>
                {t("document_inventory_write_off.sale_locality")}
                {localityRequired ? " *" : ""}
              </label>
              <FieldError
                touched={formik.touched.saleSAtsLocalityId}
                error={formik.errors.saleSAtsLocalityId}
                fallback={t("documentsError.required")}
              />
            </div>
            <Field
              component={SelectCustomFormik}
              name="saleSAtsLocalityId"
              placeholder={t("documentLabels.choose")}
              size="sm"
              isMulti={false}
              options={localityOptions}
              isDisabled={disabled || !formik.values.saleSAtsDistrictId}
              isError={Boolean(formik.touched.saleSAtsLocalityId && formik.errors.saleSAtsLocalityId)}
              onInputChange={searchLocalities}
              noOptionsMessage={noOptionsMessageText}
              onChange={(id: any) => {
                const opt = findOpt(localityOptions, id);
                clearFrom("geonim");
                formik.setFieldValue("saleSAtsLocalityId", id ?? null);
                formik.setFieldValue(
                  "saleSAtsLocalityName",
                  opt?.displayName || opt?.name || opt?.label || null,
                );
                onDirty();
              }}
            />
          </div>
        )}

        <div className={style.inputField}>
          <div className={style.labelAndButtonWrapper}>
            <label className={style.inputLabel2}>
              {t("document_inventory_write_off.sale_street")}
            </label>
          </div>
          <Field
            component={SelectCustomFormik}
            name="saleSGeonimId"
            placeholder={t("documentLabels.choose")}
            size="sm"
            isMulti={false}
            options={geonimOptions}
            isDisabled={disabled || !atsIdForGeonims}
            onInputChange={searchGeonims}
            noOptionsMessage={noOptionsMessageText}
            onChange={(id: any) => {
              const opt = findOpt(geonimOptions, id);
              clearFrom("building");
              formik.setFieldValue("saleSGeonimId", id ?? null);
              formik.setFieldValue(
                "saleSGeonimName",
                opt?.displayName || opt?.name || opt?.label || null,
              );
              onDirty();
            }}
          />
        </div>

        <div className={style.inputField}>
          <div className={style.labelAndButtonWrapper}>
            <label className={style.inputLabel2}>
              {t("document_inventory_write_off.sale_building")}
            </label>
          </div>
          <Field
            component={SelectCustomFormik}
            name="saleSBuildingId"
            placeholder={t("documentLabels.choose")}
            size="sm"
            isMulti={false}
            options={buildingOptions}
            isDisabled={disabled || !formik.values.saleSGeonimId}
            onInputChange={searchBuildings}
            noOptionsMessage={noOptionsMessageFree}
            onChange={(id: any) => {
              const opt = findOpt(buildingOptions, id);
              clearFrom("premise");
              formik.setFieldValue("saleSBuildingId", id ?? null);
              formik.setFieldValue(
                "saleSBuildingName",
                opt?.displayName || opt?.name || opt?.label || null,
              );
              formik.setFieldValue("saleSBuildingRca", opt?.rca || null);
              onDirty();
            }}
          />
        </div>

        <div className={style.inputField}>
          <div className={style.labelAndButtonWrapper}>
            <label className={style.inputLabel2}>
              {t("document_inventory_write_off.sale_premise")}
            </label>
          </div>
          <Field
            component={SelectCustomFormik}
            name="saleSPbId"
            placeholder={t("documentLabels.choose")}
            size="sm"
            isMulti={false}
            options={premiseOptions}
            isDisabled={disabled || !formik.values.saleSBuildingId}
            onInputChange={searchPremises}
            noOptionsMessage={noOptionsMessageFree}
            onChange={(id: any) => {
              const opt = findOpt(premiseOptions, id);
              formik.setFieldValue("saleSPbId", id ?? null);
              formik.setFieldValue(
                "saleSPbName",
                opt?.displayName || opt?.name || opt?.label || null,
              );
              formik.setFieldValue("saleSPbRca", opt?.rca || null);
              onDirty();
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function isSaleWithdrawalReason(
  reasonId: any,
  reasons: { value: any; label: string }[],
): boolean {
  if (reasonId == null || reasonId === "") return false;
  const item = reasons.find((r) => r.value == reasonId);
  if (!item?.label) return false;
  const label = item.label.toLowerCase();
  return label.includes("продаж") || label.includes("сату");
}

export function buildSalePayload(formikValues: any, isSale: boolean) {
  if (!isSale) {
    return {
      buyerIin: null,
      saleSAtsRegionId: null,
      saleSAtsRegionName: null,
      saleSAtsDistrictId: null,
      saleSAtsDistrictName: null,
      saleSAtsLocalityId: null,
      saleSAtsLocalityName: null,
      saleSGeonimId: null,
      saleSGeonimName: null,
      saleSBuildingId: null,
      saleSBuildingName: null,
      saleSBuildingRca: null,
      saleSPbId: null,
      saleSPbName: null,
      saleSPbRca: null,
    };
  }
  return {
    buyerIin: formikValues.buyerIin || null,
    saleSAtsRegionId: formikValues.saleSAtsRegionId || null,
    saleSAtsRegionName: formikValues.saleSAtsRegionName || null,
    saleSAtsDistrictId: formikValues.saleSAtsDistrictId || null,
    saleSAtsDistrictName: formikValues.saleSAtsDistrictName || null,
    saleSAtsLocalityId: formikValues.saleSAtsLocalityId || null,
    saleSAtsLocalityName: formikValues.saleSAtsLocalityName || null,
    saleSGeonimId: formikValues.saleSGeonimId || null,
    saleSGeonimName: formikValues.saleSGeonimName || null,
    saleSBuildingId: formikValues.saleSBuildingId || null,
    saleSBuildingName: formikValues.saleSBuildingName || null,
    saleSBuildingRca: formikValues.saleSBuildingRca || null,
    saleSPbId: formikValues.saleSPbId || null,
    saleSPbName: formikValues.saleSPbName || null,
    saleSPbRca: formikValues.saleSPbRca || null,
  };
}
